/**
 * @module flow-list/components/FlowCard
 *
 * The non-table flow row used inside grouped tag sections. Reuses the same
 * cell renderers as the table (status, run strip, last-run, author, actions)
 * so a flow looks identical in both views.
 */
import React from 'react';
import { Text, type StatusTimelineEntry } from '@chatsdet/ui';
import type { FailureDiagnosticCategory } from '@chatsdet/types/issues';
import { useEligibilityModel } from '../../../../hooks/useEligibilityModel';
import type { GroupedWorkflowItem } from '../../../../hooks/useWorkflows';
import type { Tag } from '../../../../hooks/useTags';
import type { OpenWorkflowIssueSummary } from '../types';
import { formatRelativeTime } from '../../FlowDesigner/runs/run-formatters';
import {
  FavoriteStar,
  FlowActionsMenu,
  FlowAuthorCell,
  FlowLastRunDurationCell,
  FlowRecentRuns,
  FlowLifecycleIcons,
  FlowTagsCell,
  type FlowActionHandlers,
  type LatestRunInfo,
} from './cells';

export type FlowCardProps = {
  flow: GroupedWorkflowItem;
  /** Tag id of the hosting section — hidden from the card's own tag list. */
  sectionTagId: string | null;
  entries: StatusTimelineEntry[];
  /** See {@link FlowRecentRuns}'s `diagnosedCategory` prop. */
  diagnosedCategory: FailureDiagnosticCategory | null;
  /** See {@link FlowRecentRuns}'s `openIssues` prop. */
  openIssues: OpenWorkflowIssueSummary[];
  /**
   * Fallback last-run timestamp from the recent-runs feed, for environments
   * where `workflows.last_run_id` isn't populated (matches the table's
   * "Last run" source).
   */
  latestRun: LatestRunInfo | undefined;
  isRunsFetching: boolean;
  member: { displayName: string | null; primaryEmail: string | null } | null | undefined;
  actionHandlers: FlowActionHandlers;
  onToggleFavorite: (workflowId: string, next: boolean) => void;
  onOpen: (workflowId: string, modifiers?: { metaKey: boolean; ctrlKey: boolean }) => void;
};

export function FlowCard({
  flow,
  sectionTagId,
  entries,
  diagnosedCategory,
  openIssues,
  latestRun,
  isRunsFetching,
  member,
  actionHandlers,
  onToggleFavorite,
  onOpen,
}: FlowCardProps) {
  const eligibilityModel = useEligibilityModel();
  const lastRunTs = flow.lastRunAt ?? latestRun?.ts ?? null;
  const lastRunDurationMs = flow.lastRunDurationMs ?? latestRun?.durationMs ?? null;
  // The hosting section's tag is implied by context — only surface the others.
  const otherTags = React.useMemo<Tag[]>(
    () =>
      flow.tags
        .filter((t) => t.id !== sectionTagId)
        .map((t) => ({
          id: t.id,
          name: t.name,
          color: t.color as Tag['color'],
          orgId: '',
          description: null,
          createdAt: '',
          updatedAt: '',
          createdBy: null,
        })),
    [flow.tags, sectionTagId]
  );

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={(e) => onOpen(flow.id, { metaKey: e.metaKey, ctrlKey: e.ctrlKey })}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(flow.id);
      }}
      className="group flex items-center gap-2.5 px-3 py-2 rounded-lg border border-border/60 bg-card hover:bg-muted/30 hover:border-border transition-colors cursor-pointer"
    >
      <FavoriteStar
        isFavorite={flow.isFavorite}
        onToggle={() => onToggleFavorite(flow.id, !flow.isFavorite)}
      />

      <div className="flex items-center gap-2.5 flex-1 min-w-0">
        <Text
          weight="medium"
          className="truncate cofactor:text-[13.5px]"
        >
          {flow.name || 'Untitled Workflow'}
        </Text>
        <span className="shrink-0">
          <FlowLifecycleIcons item={flow} eligibilityModel={eligibilityModel} />
        </span>
        {otherTags.length > 0 && (
          <span className="hidden lg:block shrink-0">
            <FlowTagsCell tags={otherTags} />
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <span className="hidden sm:block">
          <FlowRecentRuns
            entries={entries}
            health={flow.health}
            isPageLoading={isRunsFetching}
            diagnosedCategory={diagnosedCategory}
            openIssues={openIssues}
          />
        </span>
        <span className="hidden md:block w-16 text-right">
          <FlowLastRunDurationCell durationMs={lastRunDurationMs} />
        </span>
        <span className="hidden md:block w-16 text-right">
          {lastRunTs ? (
            <span className="text-muted-foreground text-sm cofactor:font-mono cofactor:text-[11px] tabular-nums">
              {formatRelativeTime(lastRunTs)}
            </span>
          ) : (
            <Text size="sm" className="text-muted-foreground">
              —
            </Text>
          )}
        </span>
        <span className="hidden md:block">
          <FlowAuthorCell member={member} />
        </span>
        <FlowActionsMenu
          workflow={flow}
          handlers={actionHandlers}
          eligibilityModel={eligibilityModel}
        />
      </div>
    </div>
  );
}
